// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\doctors\EditDoctorPage\FormFields.js

import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { 
  Input, Select, Form, Divider, 
  Button, List, Typography, Space, Row, Col, Alert  
} from "antd"; 
import { debounce } from "lodash";
import { getRegions } from "../../../redux/slices/regions/regionsThunk";
import { getSpecialtysList } from "../../../redux/slices/specialtys/specialtysThunk";
import { getGovernments } from "../../../redux/slices/governments/governmentsThunk";
import { 
  StarFilled, DeleteOutlined, PlusOutlined, 
  WarningOutlined, RocketOutlined, UserOutlined, StopOutlined 
} from "@ant-design/icons";

const { Option } = Select;
const { Text } = Typography;

const FormFields = ({ form, onAddPartnerNote, onDeletePartnerNote }) => {
  const { specialtysList, governments, regions, loading } = useSelector(
    (state) => ({
      specialtysList: state.specialtys.specialtys,
      governments: state.governments.governments,
      regions: state.regions.regions,
      loading: {
        persons: state.persons.loading,
        specialtys: state.specialtys.loading,
        governments: state.governments.loading,
        regions: state.regions.loading,
      },
    })
  );

  const [search, setSearch] = useState({
    government: "",
    region: "",
    specialty: "",
  });
  const [ids, setIds] = useState({
    government: "",
    region: "",
    specialty: "",
  });

  const dispatch = useDispatch();

  const debounceSearch = useCallback(
    debounce((value, key) => {
      setSearch((prevSearch) => ({
        ...prevSearch,
        [key]: value,
      }));
    }, 300),
    []
  );

  useEffect(() => {
    dispatch(getGovernments({ page: 1, limit: 50, search: search.government }));
  }, [search.government, dispatch]);

  useEffect(() => {
    const selectedGovernmentId = ids.government || null;
    dispatch(
      getRegions({
        page: 1,
        itemsPerPage: 50,
        governmentId: selectedGovernmentId,
        search: search.region || "",
      })
    );
  }, [ids.government, search.region, dispatch]);

  useEffect(() => {
    dispatch(
      getSpecialtysList({
        page: 1,
        itemsPerPage: 50,
        filter: { search: search.specialty },
      })
    );
  }, [search.specialty, dispatch]);

  const handleSearchedValue = (value, key) => {
    debounceSearch(value, key);
  };

  const handleSelect = (key, id) => {
    setIds((prevId) => ({
      ...prevId,
      [key]: id,
    }));
  };

  return (
    <div>
      {loading.persons && <p>Chargement...</p>}

      {[
        { name: "firstname", label: "Nom", placeholder: "Nom" },
        { name: "lastname", label: "Prénom", placeholder: "Prénom" },
        { name: "email", label: "Email", placeholder: "Email" },
        { name: "phone", label: "Téléphone", placeholder: "Téléphone" },
        { name: "address", label: "Adresse", placeholder: "Adresse" },
      ].map(({ name, label, placeholder }) => (
        <Form.Item
          key={name}
          name={name}
          label={label}
          rules={[
            { required: true, message: `Veuillez entrer ${label.toLowerCase()}` },
          ]}
        >
          <Input placeholder={placeholder} />
        </Form.Item>
      ))}

      {/* --- COORDONNÉES GPS --- */}
      <Form.Item label="Coordonnées GPS">
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="latitude"
              rules={[{ pattern: /^-?\d+([.,]\d+)?$/, message: "Format : 36.8065" }]}
            >
              <Input placeholder="Latitude (ex: 36.8065)" allowClear />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="longitude"
              rules={[{ pattern: /^-?\d+([.,]\d+)?$/, message: "Format : 10.1815" }]}
            >
              <Input placeholder="Longitude (ex: 10.1815)" allowClear />
            </Form.Item>
          </Col>
        </Row>
      </Form.Item>

      <Form.Item
        name="potential"
        label="Potentiel"
        rules={[{ required: false }]}
      >
        <Select placeholder="Choisir le potentiel">
          <Option value="A+">A+</Option>
          <Option value="A">A</Option>
          <Option value="B+">B+</Option>
          <Option value="B">B</Option>
          <Option value="C">C</Option>
        </Select>
      </Form.Item>

      <Form.Item
        name="specialty"
        label="Spécialité"
        rules={[{ required: true, message: "Veuillez sélectionner une spécialité" }]}
      >
        <Select
          placeholder={loading.specialtys ? "Chargement..." : "Choisir une spécialité"}
          loading={loading.specialtys}
          showSearch
          onSearch={(e) => handleSearchedValue(e, "specialty")}
          onSelect={(e) => handleSelect("specialty", e)}
          filterOption={false}
        >
          {specialtysList?.data?.map(({ id, name }) => (
            <Option key={id} value={id}>
              {name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="government"
        label="Gouvernorat"
        rules={[{ required: true, message: "Veuillez sélectionner le gouvernorat" }]}
      >
        <Select
          placeholder={loading.governments ? "Chargement..." : "Choisir un gouvernorat"}
          showSearch
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
          loading={loading.governments}
        >
          {governments?.data?.map(({ id, name }) => (
            <Option key={id} value={id}>
              {name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="region"
        label="Région"
        rules={[{ required: true, message: "Veuillez sélectionner une région" }]}
      >
        <Select
          placeholder={loading.regions ? "Chargement..." : "Choisir une région"}
          loading={loading.regions}
          showSearch
          onSearch={(e) => handleSearchedValue(e, "region")}
          onSelect={(e) => handleSelect("region", e)}
          filterOption={false}
        >
          {regions?.data?.map(({ id, name }) => (
            <Option key={id} value={id}>
              {name}
            </Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="doctorType"
        label="Type"
        rules={[{ required: true, message: "Veuillez sélectionner un type" }]}
      >
        <Select placeholder="Choisir un type">
          <Option value={2}>Privé</Option>
          <Option value={1}>Hospitalier</Option>
        </Select>
      </Form.Item>

      {/* --- ⚡ SECTION CIBLAGE, PARTENARIAT & PRESCRIPTION ⚡ --- */}
      <Divider orientation="left" style={{ borderColor: '#fadb14', color: '#d4b106' }}>
        <StarFilled /> Profil & Partenariat
      </Divider>

      <Form.Item 
        name="relationshipCategory" 
        label="Profil Relationnel" 
        rules={[{ required: true, message: "Veuillez choisir un profil" }]}
      >
        <Select style={{ width: '100%' }}>
          <Option value="PARTNER_ACTIVE">
            <span style={{ color: '#d4b106', fontWeight: 'bold' }}>
              <StarFilled style={{ marginRight: 6 }} /> ⭐ Partenaire Stratégique (Prescrit + Congrès/Voyages)
            </span>
          </Option>
          <Option value="PARTNER_AT_RISK">
            <span style={{ color: '#fa8c16', fontWeight: 'bold' }}>
              <WarningOutlined style={{ marginRight: 6 }} /> ⚠️ Partenaire Sous Vigilance (Défaillant / Parti concurrent)
            </span>
          </Option>
          <Option value="KEY_PRESCRIBER">
            <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
              <RocketOutlined style={{ marginRight: 6 }} /> 🚀 Prescripteur Clé (A+) (Non partenaire mais fort prescripteur)
            </span>
          </Option>
          <Option value="STANDARD">
            <span style={{ color: '#595959' }}>
              <UserOutlined style={{ marginRight: 6 }} /> 👤 Standard (Potentiel moyen / Visite d'entretien)
            </span>
          </Option>
          <Option value="REFRACTORY">
            <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
              <StopOutlined style={{ marginRight: 6 }} /> ⛔ Non Réceptif (Refuse visites / 100% concurrent)
            </span>
          </Option>
        </Select>
      </Form.Item>

      {/* BLOC HISTORIQUE PARTENARIAT (CONGRÈS / VOYAGES / ENGAGEMENTS) */}
      <Form.Item 
        noStyle 
        shouldUpdate={(prev, curr) => 
          prev.relationshipCategory !== curr.relationshipCategory || 
          prev.partnerNotes !== curr.partnerNotes
        }
      >
        {({ getFieldValue }) => {
          const category = getFieldValue('relationshipCategory');
          const isPartnerProfile = category === 'PARTNER_ACTIVE' || category === 'PARTNER_AT_RISK';
          const isAtRisk = category === 'PARTNER_AT_RISK';

          return isPartnerProfile ? (
            <div style={{ 
              marginLeft: '33%', 
              marginBottom: '20px', 
              padding: '16px', 
              background: isAtRisk ? '#fff2e8' : '#fffbe6', 
              borderRadius: '8px', 
              border: isAtRisk ? '1px solid #ffbb96' : '1px solid #ffe58f' 
            }}>
              {isAtRisk && (
                <Alert
                  type="warning"
                  showIcon
                  message="Médecin sous surveillance commerciale"
                  description="Ce médecin a bénéficié d'accords labo ou congrès mais ne prescrit plus nos gammes. Renseignez les motifs ci-dessous."
                  style={{ marginBottom: 12 }}
                />
              )}

              <Text strong style={{ color: isAtRisk ? '#d4380d' : '#856404' }}>
                Historique Partenaire, Congrès & Engagements :
              </Text>
              
              <List
                size="small"
                style={{ marginTop: '10px', background: '#fff' }}
                bordered
                dataSource={getFieldValue('partnerNotes') ? getFieldValue('partnerNotes').split('\n').filter(Boolean) : []}
                renderItem={(item, index) => (
                  <List.Item actions={[
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => onDeletePartnerNote(index)} />
                  ]}>
                    {item}
                  </List.Item>
                )}
              />

              <Space.Compact style={{ width: '100%', marginTop: '10px' }}>
                <Input 
                  id="newPartnerNoteInput" 
                  placeholder="Ajouter (ex: Congrès Milan 2025, Accord Pédiatrie, etc.)" 
                  onPressEnter={(e) => { e.preventDefault(); onAddPartnerNote(); }}
                />
                <Button type="primary" onClick={onAddPartnerNote} icon={<PlusOutlined />}>
                  Ajouter
                </Button>
              </Space.Compact>
              
              <Form.Item name="partnerNotes" hidden><Input /></Form.Item>
            </div>
          ) : null;
        }}
      </Form.Item>
    </div>
  );
};

export default FormFields;